/**
 * Focus / scroll targets for My company guided setup.
 * Only IDs that are actually rendered on the corresponding pages.
 */

import {
  OPEN_BOUNDARIES_EDITOR_EVENT,
  ORG_BOUNDARY_FOCUS,
  ORG_BOUNDARY_ISSUE,
  ORG_BOUNDARY_OWNERSHIP_ISSUE_PREFIX,
  REPORTING_ENTITY_DESCRIPTION_FIELD_ID,
} from '../components/ghg/orgBoundaryFocus';

const EXACT_FOCUS_IDS: Record<string, string> = {
  'Company name': 'essentials-company-name',
  'GST number': 'essentials-gst-number',
  'Export activity': 'export-activity-preference',
  'Primary energy source': 'facility-primary-energy-source',
  'Main fuels used': 'facility-main-fuels',
  'Electricity supplier': 'facility-electricity-supplier',
  'Manufacturing unit name': 'workflow-unit-name',
  'Products manufactured': 'workflow-unit-products',
  'Save direct emissions': 'save-direct-emissions',
  'Save value chain': 'save-value-chain',
  'What stays outside the value chain': 'scope3-out-of-boundary',
};

const EXACT_FOCUS_TEST_IDS: Record<string, string> = {
  'Direct emission sources': 'ops-boundary-period-scope1',
  'Value-chain categories': 'ops-boundary-scope3',
};

const HIGHLIGHT_CLASS = 'my-company-focus-highlight';
const HIGHLIGHT_MS = 2800;
const SCROLL_MARGIN_PX = 96;

export type MyCompanyFocusTarget = {
  id?: string;
  testId?: string;
  /** Prefer highlighting this wrapper while focusing a child. */
  highlightId?: string;
  highlightTestId?: string;
  /** CSS selector for the focusable control inside the target. */
  focusSelector?: string;
  /** Open Boundaries accordion before resolving DOM nodes. */
  openBoundariesEditor?: boolean;
};

const BOUNDARY_ISSUE_TARGETS: Record<string, MyCompanyFocusTarget> = {
  [ORG_BOUNDARY_ISSUE.consolidation]: {
    id: ORG_BOUNDARY_FOCUS.consolidation,
    testId: ORG_BOUNDARY_FOCUS.consolidation,
    focusSelector: 'button[data-testid^="org-approach-"]',
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.reportingEntity]: {
    id: REPORTING_ENTITY_DESCRIPTION_FIELD_ID,
    testId: REPORTING_ENTITY_DESCRIPTION_FIELD_ID,
    highlightId: ORG_BOUNDARY_FOCUS.reportingEntityGroup,
    highlightTestId: ORG_BOUNDARY_FOCUS.reportingEntityGroup,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.jvAllocation]: {
    id: ORG_BOUNDARY_FOCUS.jvAllocation,
    testId: ORG_BOUNDARY_FOCUS.jvAllocation,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.franchises]: {
    id: `${ORG_BOUNDARY_FOCUS.franchises}-input`,
    testId: `${ORG_BOUNDARY_FOCUS.franchises}-input`,
    highlightId: ORG_BOUNDARY_FOCUS.franchises,
    highlightTestId: ORG_BOUNDARY_FOCUS.franchises,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.singleOrRelated]: {
    id: ORG_BOUNDARY_FOCUS.relatedParties,
    testId: ORG_BOUNDARY_FOCUS.relatedParties,
    focusSelector: `#${ORG_BOUNDARY_FOCUS.singleLegalEntity}`,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.fuel]: {
    id: ORG_BOUNDARY_FOCUS.fuel,
    testId: ORG_BOUNDARY_FOCUS.fuel,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.fleet]: {
    id: ORG_BOUNDARY_FOCUS.fleet,
    testId: ORG_BOUNDARY_FOCUS.fleet,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.logistics]: {
    id: ORG_BOUNDARY_FOCUS.logistics,
    testId: ORG_BOUNDARY_FOCUS.logistics,
    openBoundariesEditor: true,
  },
  [ORG_BOUNDARY_ISSUE.equityPercent]: {
    id: ORG_BOUNDARY_FOCUS.equityPercent,
    testId: ORG_BOUNDARY_FOCUS.equityPercent,
    highlightTestId: 'org-boundary-equity-percent-group',
    openBoundariesEditor: true,
  },
};

let highlightClearTimer: number | null = null;
let highlightClearListeners: Array<() => void> = [];

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ensureHighlightStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('my-company-focus-styles')) return;
  const style = document.createElement('style');
  style.id = 'my-company-focus-styles';
  style.textContent = `
    [data-my-company-focus-target] {
      scroll-margin-top: ${SCROLL_MARGIN_PX}px;
    }
    .${HIGHLIGHT_CLASS} {
      outline: 3px solid #0f766e !important;
      outline-offset: 4px;
      border-radius: 8px;
      scroll-margin-top: ${SCROLL_MARGIN_PX}px;
      box-shadow: 0 0 0 6px rgba(15, 118, 110, 0.18);
    }
    @media (prefers-reduced-motion: no-preference) {
      .${HIGHLIGHT_CLASS} {
        animation: my-company-focus-pulse 1.1s ease-out 1;
      }
    }
    @keyframes my-company-focus-pulse {
      from { box-shadow: 0 0 0 0 rgba(15, 118, 110, 0.4); }
      to { box-shadow: 0 0 0 10px rgba(15, 118, 110, 0); }
    }
  `;
  document.head.appendChild(style);
}

function clearFocusHighlight(): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((node) => {
    node.classList.remove(HIGHLIGHT_CLASS);
    node.removeAttribute('data-my-company-focus-active');
  });
  highlightClearListeners.forEach((off) => off());
  highlightClearListeners = [];
  if (highlightClearTimer != null) {
    window.clearTimeout(highlightClearTimer);
    highlightClearTimer = null;
  }
}

function scheduleHighlightClear(root: HTMLElement): void {
  if (highlightClearTimer != null) {
    window.clearTimeout(highlightClearTimer);
  }
  highlightClearTimer = window.setTimeout(() => {
    root.classList.remove(HIGHLIGHT_CLASS);
    root.removeAttribute('data-my-company-focus-active');
    highlightClearTimer = null;
  }, HIGHLIGHT_MS);

  const clearOnInteract = () => {
    root.classList.remove(HIGHLIGHT_CLASS);
    root.removeAttribute('data-my-company-focus-active');
    if (highlightClearTimer != null) {
      window.clearTimeout(highlightClearTimer);
      highlightClearTimer = null;
    }
    highlightClearListeners.forEach((off) => off());
    highlightClearListeners = [];
  };

  const onPointer = () => clearOnInteract();
  const onKey = (event: Event) => {
    const key = (event as KeyboardEvent).key;
    if (key === 'Tab' || key === 'Escape') return;
    clearOnInteract();
  };
  root.addEventListener('pointerdown', onPointer, { once: true });
  root.addEventListener('keydown', onKey, { once: true });
  highlightClearListeners.push(() => {
    root.removeEventListener('pointerdown', onPointer);
    root.removeEventListener('keydown', onKey);
  });
}

function queryByIdOrTestId(id?: string, testId?: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  if (id) {
    const byId = document.getElementById(id);
    if (byId instanceof HTMLElement) return byId;
  }
  if (testId) {
    const byTestId = document.querySelector(`[data-testid="${testId}"]`);
    if (byTestId instanceof HTMLElement) return byTestId;
  }
  return null;
}

function findFocusable(root: HTMLElement, selector?: string): HTMLElement | null {
  if (selector) {
    const match = root.querySelector(selector);
    if (match instanceof HTMLElement) return match;
  }
  if (
    root instanceof HTMLInputElement
    || root instanceof HTMLTextAreaElement
    || root instanceof HTMLSelectElement
    || root instanceof HTMLButtonElement
    || root.isContentEditable
  ) {
    return root;
  }
  // Prefer native fields over chip/button groups (avoid stealing chip ripple focus).
  const field = root.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
  );
  if (field) return field;
  // Option groups use tabIndex={-1} on the highlight root — focus the group itself.
  if (root.getAttribute('tabindex') != null) return root;
  const nested = root.querySelector<HTMLElement>(
    'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  return nested;
}

function applyFocusHighlight(target: MyCompanyFocusTarget): boolean {
  ensureHighlightStyles();
  const highlightEl = queryByIdOrTestId(target.highlightId, target.highlightTestId)
    || queryByIdOrTestId(target.id, target.testId);
  const anchor = queryByIdOrTestId(target.id, target.testId) || highlightEl;
  if (!anchor && !highlightEl) return false;

  const root = highlightEl || anchor!;
  const focusEl = (anchor && findFocusable(anchor, target.focusSelector))
    || findFocusable(root, target.focusSelector)
    || (root.tabIndex >= -1 ? root : null);

  clearFocusHighlight();
  root.classList.add(HIGHLIGHT_CLASS);
  root.setAttribute('data-my-company-focus-active', 'true');

  const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
  root.scrollIntoView({ behavior, block: 'center' });

  if (focusEl) {
    if (focusEl.tabIndex < 0 && focusEl.getAttribute('tabindex') == null) {
      focusEl.tabIndex = -1;
    }
    try {
      focusEl.focus({ preventScroll: true });
    } catch {
      focusEl.focus();
    }
  } else {
    if (root.getAttribute('tabindex') == null) root.tabIndex = -1;
    try {
      root.focus({ preventScroll: true });
    } catch {
      root.focus();
    }
  }

  scheduleHighlightClear(root);
  return true;
}

function resolveBoundaryIssueTarget(missingLabel: string): MyCompanyFocusTarget | null {
  const exact = BOUNDARY_ISSUE_TARGETS[missingLabel];
  if (exact) return exact;

  if (missingLabel.startsWith(ORG_BOUNDARY_OWNERSHIP_ISSUE_PREFIX)) {
    return {
      id: ORG_BOUNDARY_FOCUS.relatedOwnership,
      testId: ORG_BOUNDARY_FOCUS.relatedOwnership,
      focusSelector: 'input[data-testid="org-holding-ownership-percent"], input[type="text"], input[type="number"]',
      openBoundariesEditor: true,
    };
  }

  const lower = missingLabel.toLowerCase();
  if (lower.includes('organizational') || lower.includes('boundary')) {
    return {
      id: 'save-organizational-boundary',
      testId: 'org-boundary-ease',
      openBoundariesEditor: true,
    };
  }
  return null;
}

export function resolveMyCompanyFocusTarget(missingLabel: string): MyCompanyFocusTarget | null {
  const exactId = EXACT_FOCUS_IDS[missingLabel];
  if (exactId) return { id: exactId };
  const exactTestId = EXACT_FOCUS_TEST_IDS[missingLabel];
  if (exactTestId) return { testId: exactTestId };

  return resolveBoundaryIssueTarget(missingLabel);
}

export function focusMyCompanyMissing(missingLabel: string): boolean {
  const target = resolveMyCompanyFocusTarget(missingLabel);
  if (!target) return false;

  if (typeof window !== 'undefined' && target.openBoundariesEditor) {
    window.dispatchEvent(new CustomEvent(OPEN_BOUNDARIES_EDITOR_EVENT));
  }

  if (applyFocusHighlight(target)) return true;

  // Editor may mount after the open event — retry briefly.
  if (typeof window === 'undefined') return false;
  window.requestAnimationFrame(() => {
    if (applyFocusHighlight(target)) return;
    window.setTimeout(() => {
      if (!applyFocusHighlight(target)) {
        window.setTimeout(() => {
          applyFocusHighlight(target);
        }, 120);
      }
    }, 40);
  });
  return true;
}

export function myCompanyMissingHasFocusTarget(missingLabel: string): boolean {
  return resolveMyCompanyFocusTarget(missingLabel) != null;
}

/** Test helper — clears temporary highlight state. */
export function __clearMyCompanyFocusHighlightForTests(): void {
  clearFocusHighlight();
}
